import { yupResolver } from '@hookform/resolvers/yup';
import { useShopsysForm } from 'hooks/forms/UseShopsysForm';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { PromoCodeFormType } from 'types/form';
import { object, string } from 'yup';

export const usePromoCodeForm = (): [UseFormReturn<PromoCodeFormType>, PromoCodeFormType] => {
    const t = useTypedTranslationFunction();

    const resolver = yupResolver(
        object().shape({
            promoCode: string().required(t('This field is required')),
        }),
    );

    const defaultValues = { promoCode: '' };

    return [useShopsysForm(resolver, defaultValues), defaultValues];
};

type PromoCodeFormMeta = {
    formName: string;
    messages: {
        addPromoCode: {
            success: string;
            error: string;
        };
        removePromoCode: {
            success: string;
            error: string;
        };
    };
    fields: {
        [key in keyof PromoCodeFormType]: {
            name: key;
            label: string;
            errorMessage: string | undefined;
        };
    };
};

export const usePromoCodeFormMeta = (formProviderMethods: UseFormReturn<PromoCodeFormType>): PromoCodeFormMeta => {
    const t = useTypedTranslationFunction();

    return useMemo(
        () => ({
            formName: 'promo-code-form',
            messages: {
                addPromoCode: {
                    success: t('Promo code was added to the order.'),
                    error: t('There was an error while adding a promo code to the order.'),
                },
                removePromoCode: {
                    success: t('Promo code was removed from the order.'),
                    error: t('There was an error while removing the promo code from the order.'),
                },
            },
            fields: {
                promoCode: {
                    name: 'promoCode' as const,
                    label: t('Coupon'),
                    errorMessage: formProviderMethods.formState.errors.promoCode?.message,
                },
            },
        }),
        [formProviderMethods.formState.errors.promoCode?.message, t],
    );
};
